import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Box, Button, CircularProgress } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Color } from '../../../GlobalStyles';
import Service from '../../../services/httpService';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const B2CReportDownload = () => {
    const service = new Service();
    const [selectedMonth, setSelectedMonth] = useState(dayjs());
    const [downloading, setDownloading] = useState(false);

    // The backend sends the canonical name, fall back to building it ourselves.
    const getFileName = (res, fallback) => {
        const match = res.headers?.['content-disposition']?.match(/filename="?([^";]+)"?/);
        return match ? match[1] : fallback;
    };

    // With responseType blob an error body is a Blob, not JSON, so it needs unwrapping.
    const getErrorMessage = async (err) => {
        try {
            const text = await err?.response?.data?.text();
            return JSON.parse(text)?.message;
        } catch (e) {
            return null;
        }
    };

    const downloadReport = async () => {
        if (!selectedMonth || !selectedMonth.isValid()) {
            toast.error('Please select a valid month');
            return;
        }
        setDownloading(true);
        try {
            const month = selectedMonth.month() + 1; // dayjs months are 0 indexed
            const year = selectedMonth.year();
            const res = await service.getFile('/b2cReport', { month, year });

            if (Number(res.headers?.['x-record-count']) === 0) {
                toast.info(`No successful B2C transactions in ${selectedMonth.format('MMMM YYYY')}`);
                return;
            }

            const url = window.URL.createObjectURL(new Blob([res.data], { type: XLSX_MIME }));
            const link = document.createElement('a');
            link.href = url;
            link.download = getFileName(res, `B2C_${year}_${selectedMonth.format('MMMM')}_report.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            // Revoking synchronously can cancel the download in some browsers.
            setTimeout(() => window.URL.revokeObjectURL(url), 1000);
        } catch (err) {
            console.log(err);
            const message = await getErrorMessage(err);
            toast.error(message || 'Could not download the B2C report');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                    views={['year', 'month']}
                    openTo="month"
                    // label="Report month"
                    value={selectedMonth}
                    onChange={(value) => setSelectedMonth(value)}
                    maxDate={dayjs()}
                    disabled={downloading}
                    slotProps={{ textField: { size: 'small', sx: { width: '14rem' } } }}
                />
            </LocalizationProvider>
            <Button
                variant="contained"
                onClick={downloadReport}
                disabled={downloading}
                startIcon={downloading ? <CircularProgress size={16} sx={{ color: Color.white }} /> : <FileDownloadOutlinedIcon />}
                sx={{
                    textTransform: 'none',
                    background: Color.primary1,
                    '&:hover': { background: Color.primary1 }
                }}
            >
                {downloading ? 'Preparing...' : 'Download Report'}
            </Button>
            <ToastContainer position="top-right" autoClose={4000} />
        </Box>
    );
};

export default B2CReportDownload;
